import { inject, Injectable } from '@angular/core';
import { HsBlazorService, HsJsWebbridgeService } from '@hsetu/core-services';
import {
  catchError,
  filter,
  firstValueFrom,
  map,
  of,
  take,
  tap,
  timeout,
} from 'rxjs';

const SYNC_EVENT_CHANNEL = 'SYNCEVENT';

enum SyncEventType {
  Stopped = 0,
  Started = 1,
  Running = 2,
  ChangesFound = 3,
  ChangesSynced = 4,
  Completed = 5,
  Failed = 6,
  SyncingChanges = 7,
}

interface SyncEventPayload {
  type: SyncEventType;
  context: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CloudWorkerSyncService {
  private readonly _blazor = inject(HsBlazorService);
  private readonly _jsWebbridge = inject(HsJsWebbridgeService);
  private readonly _serviceKey = 'cloudworkerWebSync';

  public async startDomainSyncs() {
    await this.start('Shared', 'Kaufmann');
    await this.start('Shared', 'Project');
    await this.start('Shared', 'Contact');
    await this.start('Shared', 'Financial');
  }

  public async waitForDomainSyncReady(
    domainKeys: string[],
    timeoutMs = 30000,
  ): Promise<void> {
    const pending = new Set(domainKeys);
    if (pending.size === 0) {
      return;
    }

    await firstValueFrom(
      this._jsWebbridge.on(SYNC_EVENT_CHANNEL).pipe(
        map((message) => JSON.parse(message.data) as SyncEventPayload),
        filter(
          (event) =>
            event.type === SyncEventType.Completed ||
            event.type === SyncEventType.Failed,
        ),
        tap((event) => pending.delete(event.context)),
        filter(() => pending.size === 0),
        take(1),
        timeout(timeoutMs),
        catchError(() => {
          console.warn(
            `[CloudWorkerSync] Timed out after ${timeoutMs}ms waiting for sync readiness of: ${Array.from(pending).join(', ')}`,
          );
          return of(void 0);
        }),
      ),
    );
  }

  public stopDomainSyncs() {
    this.stopAll();
  }

  public async clearSyncSetting() {
    await this.clearAllSyncData();
  }

  public async getLastSyncDate(
    tenantId: string,
    key: string,
  ): Promise<Date | undefined> {
    const syncDateResponse = await this._blazor.invokeService(
      this._serviceKey,
      'GetLastSyncDate_V2',
      { key, tenantId },
    );

    if (syncDateResponse) {
      return (syncDateResponse as Record<string, Date | undefined>)[
        'lastSyncDate'
      ];
    }

    return undefined;
  }

  public async clearSyncData(tenantId: string, key: string): Promise<boolean> {
    const successResponse = await this._blazor.invokeService(
      this._serviceKey,
      'ClearSyncData_V2',
      { key, tenantId },
    );

    if (successResponse) {
      return (successResponse as Record<string, boolean>)['success'];
    }

    return false;
  }

  public async clearAllSyncData() {
    const successResponse = await this._blazor.invokeService(
      this._serviceKey,
      'ClearAllSyncData_V2',
    );

    if (successResponse) {
      return (successResponse as Record<string, boolean>)['success'];
    }

    return false;
  }

  public async start(tenantId: string, key: string): Promise<boolean> {
    const successResponse = await this._blazor.invokeService(
      this._serviceKey,
      'StartSync_V2',
      { key, tenantId },
    );

    if (successResponse) {
      return (successResponse as Record<string, boolean>)['success'];
    }

    return false;
  }

  private async stop(tenantId: string, key: string) {
    const successResponse = await this._blazor.invokeService(
      this._serviceKey,
      'StopSync_V2',
      { key, tenantId },
    );

    if (successResponse) {
      return (successResponse as Record<string, boolean>)['success'];
    }

    return false;
  }

  private async stopAll() {
    const successResponse = await this._blazor.invokeService(
      this._serviceKey,
      'StopAll_V2',
    );

    if (successResponse) {
      return (successResponse as Record<string, boolean>)['success'];
    }

    return false;
  }
}
